/**
 * applyFieldRearrangement
 *
 * Transforms the raw settings OrderedMap (settingsEntriesSelector output) by:
 *  1. Applying position rearrangements: field X is shown at target position Y
 *  2. Applying hidden state: hidden fields render as null (empty cell)
 *
 * Five passes:
 *  Before the passes, placements that do not fit the company are dropped, and fields with no placement
 *  whose own slot a placement claims are collected as displaced.
 *  Pass 1 — rawData.map(): transforms existing cells.
 *  Pass 2 — inject missing cells for sparse positions.
 *  Pass 3 — place displaced fields at the last available empty pair-slot.
 *  Pass 4 — apply readonly to locked fields.
 *  Pass 5 — re-key map entries so each key matches its cell's _cell address.
 *           Passes 1–4 can leave stale keys (e.g. Pass 1 uses .map() which
 *           preserves original OrderedMap keys). Consumers such as
 *           Tables.componentDidUpdate rely on data.get(addr) where addr === _cell.
 */

import { OrderedMap, Map, Set } from 'immutable';
import { pipe, Option, Array as A } from 'effect';

// Immutable Record cell — we use a loose interface since the actual Record
// types come from untyped JS factories in helper.js
interface ImmutableCell {
  get(key: string): unknown;
  set(key: string, value: unknown): ImmutableCell;
}

type RawData = OrderedMap<string, ImmutableCell>;
type PositionsMap = Map<string, string>;
type FieldSet = Set<string>;

interface ParsedAddr {
  col: string;
  row: number;
}

interface ResolvedCells {
  headerAddr: string;
  valueAddr: string;
  headerCell: ImmutableCell | null;
  valueCell: ImmutableCell | null;
}

export interface FieldLookups {
  fieldToOriginalPairHeader: Record<string, string>;
  pairHeaderToField: Record<string, string>;
  /** Maps description cell `name` → value cell field name (fallback for API-hidden fields). */
  descNameToFieldName: Record<string, string>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cellGet(cell: ImmutableCell | null | undefined, key: string): unknown {
  if (!cell) return undefined;
  return typeof cell.get === 'function' ? cell.get(key) : (cell as unknown as Record<string, unknown>)[key];
}

function parseAddr(addr: string): Option.Option<ParsedAddr> {
  return pipe(
    Option.fromNullable(addr.match(/^([A-Z]+)(\d+)$/)),
    Option.map((m) => ({ col: m[1], row: parseInt(m[2], 10) })),
  );
}

function addrSort(addr: string): string {
  return pipe(
    Option.fromNullable(addr.match(/^([A-Z]+)(\d+)$/)),
    Option.map((m) => `${m[1]}${String(m[2]).padStart(5, '0')}`),
    Option.getOrElse(() => addr),
  );
}

// ─── Lookup builders ─────────────────────────────────────────────────────────

export function buildFieldLookups(rawData: RawData): FieldLookups {
  // Index value cells (non-description) → field name at their pair header
  const valueLookups = rawData
    .filter((cell: ImmutableCell) => cellGet(cell, 'type') !== 'description')
    .filter((cell: ImmutableCell) => !!(cellGet(cell, 'name') as string))
    .reduce(
      (acc, cell: ImmutableCell) => {
        const fieldName = cellGet(cell, 'name') as string;
        const col = cellGet(cell, '_col') as string;
        const row = cellGet(cell, '_row') as number;
        const headerAddr = `${col}${row - 1}`;
        return {
          fieldToOriginalPairHeader: { ...acc.fieldToOriginalPairHeader, [fieldName]: headerAddr },
          pairHeaderToField: { ...acc.pairHeaderToField, [headerAddr]: fieldName },
        };
      },
      { fieldToOriginalPairHeader: {}, pairHeaderToField: {}, descNameToFieldName: {} } as FieldLookups,
    );

  // Index header-only pairs (description cell with no value cell below)
  const headerOnlyLookups = rawData
    .filter((cell: ImmutableCell) => cellGet(cell, 'type') === 'description')
    .filter((_cell: ImmutableCell, addr: string) => !valueLookups.pairHeaderToField[addr])
    .reduce(
      (acc, cell: ImmutableCell, addr: string) =>
        pipe(
          parseAddr(addr),
          Option.filter((parts) => !rawData.has(`${parts.col}${parts.row + 1}`)),
          Option.flatMap(() => Option.fromNullable(cellGet(cell, 'name') as string | undefined)),
          Option.map((fieldName) => ({
            fieldToOriginalPairHeader: { ...acc.fieldToOriginalPairHeader, [fieldName]: addr },
            pairHeaderToField: { ...acc.pairHeaderToField, [addr]: fieldName },
          })),
          Option.getOrElse(() => acc),
        ),
      { fieldToOriginalPairHeader: {}, pairHeaderToField: {}, descNameToFieldName: {} } as FieldLookups,
    );

  // Build fallback: description cell name → value cell field name.
  // When a field is API-hidden, its value cell may be absent from rawData.
  // The customize form stores the description cell's name (display text) as the
  // field identifier. This mapping lets us resolve that display name back to the
  // internal field name once the value cell reappears (DA-18808).
  const descNameToFieldName: Record<string, string> = {};
  rawData
    .filter((cell: ImmutableCell) => cellGet(cell, 'type') === 'description')
    .forEach((cell: ImmutableCell, addr: string) => {
      const descName = cellGet(cell, 'name') as string;
      if (!descName) return;
      const pairField = valueLookups.pairHeaderToField[addr];
      if (pairField && pairField !== descName) {
        descNameToFieldName[descName] = pairField;
      }
    });

  return {
    fieldToOriginalPairHeader: {
      ...valueLookups.fieldToOriginalPairHeader,
      ...headerOnlyLookups.fieldToOriginalPairHeader,
    },
    pairHeaderToField: { ...valueLookups.pairHeaderToField, ...headerOnlyLookups.pairHeaderToField },
    descNameToFieldName,
  };
}

// ─── Resolve remapped cells ──────────────────────────────────────────────────

function resolveRemappedCells(
  incomingField: string,
  targetPairHeaderAddr: string,
  rawData: RawData,
  fieldToOriginalPairHeader: Record<string, string>,
  hidden: FieldSet,
  shown: FieldSet | null,
  descNameToFieldName?: Record<string, string>,
): Option.Option<ResolvedCells> {
  // Try direct lookup first; fall back to description-name → field-name mapping
  // for fields stored by their display name (DA-18808).
  const resolvedFieldName = pipe(
    Option.fromNullable(fieldToOriginalPairHeader[incomingField]),
    Option.map(() => incomingField),
    Option.orElse(() =>
      pipe(
        Option.fromNullable(descNameToFieldName?.[incomingField]),
        Option.filter((fn) => !!fieldToOriginalPairHeader[fn]),
      ),
    ),
  );

  return pipe(
    resolvedFieldName,
    Option.flatMap((fieldName) => Option.fromNullable(fieldToOriginalPairHeader[fieldName])),
    Option.flatMap((srcPairHeaderAddr) =>
      pipe(
        parseAddr(srcPairHeaderAddr),
        Option.map((srcParts) => {
          const srcValueAddr = `${srcParts.col}${srcParts.row + 1}`;
          return {
            srcHeaderCell: rawData.get(srcPairHeaderAddr) || null,
            srcValueCell: rawData.get(srcValueAddr) || null,
          };
        }),
      ),
    ),
    Option.filter(({ srcHeaderCell, srcValueCell }) => {
      const refCell = srcValueCell || srcHeaderCell;
      const isApiHidden = refCell ? cellGet(refCell, 'hidden') === true : false;
      const isExplicitlyShown = shown && shown.has(incomingField);
      return !hidden.has(incomingField) && !(isApiHidden && !isExplicitlyShown);
    }),
    Option.flatMap(({ srcHeaderCell, srcValueCell }) =>
      pipe(
        parseAddr(targetPairHeaderAddr),
        Option.map((targetParts) => {
          const targetValueAddr = `${targetParts.col}${targetParts.row + 1}`;
          return {
            headerAddr: targetPairHeaderAddr,
            valueAddr: targetValueAddr,
            headerCell: srcHeaderCell
              ? srcHeaderCell
                  .set('_cell', targetPairHeaderAddr)
                  .set('_col', targetParts.col)
                  .set('_row', targetParts.row)
              : null,
            valueCell: srcValueCell
              ? srcValueCell
                  .set('_cell', targetValueAddr)
                  .set('_col', targetParts.col)
                  .set('_row', targetParts.row + 1)
              : null,
          };
        }),
      ),
    ),
  );
}

// ─── Grid positions ──────────────────────────────────────────────────────────

function buildAllPairPositions(rawData: RawData): string[] {
  const { cols, maxRow, minRow } = rawData.reduce(
    (acc, cell: ImmutableCell) => ({
      cols: acc.cols.add(cellGet(cell, '_col') as string),
      maxRow: Math.max(acc.maxRow, cellGet(cell, '_row') as number),
      minRow: Math.min(acc.minRow, cellGet(cell, '_row') as number),
    }),
    { cols: Set<string>(), maxRow: 0, minRow: Infinity },
  );

  const sortedCols = [...cols].sort();
  const startRow = minRow % 2 === 1 ? minRow : minRow - 1;
  const headerRows = A.makeBy(Math.floor((maxRow - startRow) / 2) + 1, (i) => startRow + i * 2);

  return A.flatMap(headerRows, (headerRow) => A.map(sortedCols, (col) => `${col}${headerRow}`));
}

// ─── Field name aliases (DA-18808) ───────────────────────────────────────────

/**
 * Every name a field's settings can be stored under: its internal name and its description name. While the
 * backend hides a field without its value cell, Customize form knows it only by its description name, so a
 * hide, show or move saved then is keyed by that name.
 */
export function fieldNameAliases(rawData: RawData, fieldName: string): string[] {
  const { descNameToFieldName } = buildFieldLookups(rawData);
  return pipe(
    Object.entries(descNameToFieldName),
    A.filter(([descName, internalName]) => descName === fieldName || internalName === fieldName),
    A.flatMap(([descName, internalName]) => [descName, internalName]),
    A.filter((name) => name !== fieldName),
    A.dedupe,
  );
}

/** The set with every name's aliases added, so a lookup by either name of a field finds it. */
export function withFieldAliases(rawData: RawData, names: FieldSet): FieldSet {
  const { descNameToFieldName } = buildFieldLookups(rawData);
  return pipe(
    Object.entries(descNameToFieldName),
    A.reduce(names, (acc, [descName, internalName]) =>
      names.has(descName) || names.has(internalName) ? acc.add(descName).add(internalName) : acc,
    ),
  );
}

// ─── Placements that fit the company ─────────────────────────────────────────

/**
 * Keeps only the placements that fit this company's header: the target pair is one of `gridPairIds`, and the
 * field exists in `rawData` (by its name or, DA-18808, by its description name).
 *
 * A configuration propagated from another company (DA-20255) can place a field on a header line this company
 * does not have (ERP custom header fields), or name a custom field it does not have. Such a placement would
 * take the field out of its own slot and put it where nothing is drawn, or empty the slot it names.
 * Ignoring it keeps the field in its own slot; the stored configuration is not changed.
 */
export function fittingPositions(
  rawData: RawData,
  storedPositions: PositionsMap,
  gridPairIds: Set<string>,
): PositionsMap {
  const { fieldToOriginalPairHeader, descNameToFieldName } = buildFieldLookups(rawData);

  // A placement stored under a field's label name (DA-18808) is that field's placement; one stored under its
  // internal name is newer (Customize form writes the internal name once it knows it) and wins. Keyed by one name,
  // both views take the field out of its own slot and show it once.
  const labelKeyed = storedPositions
    .filter((_target: string, name: string) => {
      const internalName = descNameToFieldName[name];
      return !!internalName && !storedPositions.has(internalName);
    })
    .mapKeys((name: string) => descNameToFieldName[name]);
  const positions = storedPositions
    .filter((_target: string, name: string) => !descNameToFieldName[name])
    .merge(labelKeyed);

  return positions.filter(
    (target: string, fieldName: string) => gridPairIds.has(target) && !!fieldToOriginalPairHeader[fieldName],
  );
}

// ─── Main transform ──────────────────────────────────────────────────────────

export function applyFieldRearrangement(
  rawData: RawData,
  storedPositions: PositionsMap,
  storedHidden: FieldSet,
  storedShown: FieldSet,
  locked?: FieldSet,
): RawData {
  const positions = fittingPositions(rawData, storedPositions, Set(buildAllPairPositions(rawData)));
  // A field hidden or shown under either of its names is hidden or shown (DA-18808, DA-20255).
  const hidden = withFieldAliases(rawData, storedHidden);
  const shown = withFieldAliases(rawData, storedShown);
  const { fieldToOriginalPairHeader, pairHeaderToField, descNameToFieldName } = buildFieldLookups(rawData);

  // Reverse lookup: internal field name → display name used in positions (DA-18808).
  const fieldNameToDescName: Record<string, string> = {};
  Object.entries(descNameToFieldName).forEach(([descName, fieldName]) => {
    fieldNameToDescName[fieldName] = descName;
  });

  /** Check if a field has a positions entry (by internal name or by display name). */
  const fieldHasPosition = (fieldName: string): boolean =>
    positions.has(fieldName) || (!!fieldNameToDescName[fieldName] && positions.has(fieldNameToDescName[fieldName]));

  /** A field that would render if placed: not user-hidden, and not API-hidden unless shown. */
  const isRenderable = (fieldName: string): boolean =>
    Option.isSome(
      resolveRemappedCells(
        fieldName,
        fieldToOriginalPairHeader[fieldName],
        rawData,
        fieldToOriginalPairHeader,
        hidden,
        shown,
        descNameToFieldName,
      ),
    );

  const targetToField = positions.reduce(
    (acc: Record<string, string>, targetPairHeaderAddr: string, fieldName: string) => ({
      ...acc,
      [targetPairHeaderAddr]: fieldName,
    }),
    {},
  );

  // Fields with no placement whose own slot a placement claims. Collected from every claimed slot, not only from
  // the slot a moved field left: once placements that do not fit are ignored (DA-20255), a chain of moves can be
  // open (fa→B1, fb→C1, fc ignored), and C1's own field would otherwise be dropped.
  // Only fields that render are kept, so a hidden one does not take the empty slot a visible one needs.
  const displacedFields = pipe(
    Object.entries(pairHeaderToField),
    A.filter(
      ([homeAddr, fieldName]) =>
        targetToField[homeAddr] !== undefined && targetToField[homeAddr] !== fieldName && !fieldHasPosition(fieldName),
    ),
    A.map(([, fieldName]) => fieldName),
    A.filter(isRenderable),
  );

  // ── Pass 1: transform existing rawData cells ─────────────────────────────
  const pass1Result: RawData = (rawData as OrderedMap<string, ImmutableCell | null>)
    .map((cell: ImmutableCell | null, addr: string) => {
      if (!cell) return null;
      const type = cellGet(cell, 'type') as string;
      const col = cellGet(cell, '_col') as string;
      const row = cellGet(cell, '_row') as number;

      const pairHeaderAddr = type === 'description' ? addr : `${col}${row - 1}`;
      const isHeaderCell = type === 'description';

      // CASE A: A field is mapped TO this position
      const incomingField = targetToField[pairHeaderAddr];
      if (incomingField !== undefined) {
        return pipe(
          resolveRemappedCells(
            incomingField,
            pairHeaderAddr,
            rawData,
            fieldToOriginalPairHeader,
            hidden,
            shown,
            descNameToFieldName,
          ),
          Option.map((resolved) => (isHeaderCell ? resolved.headerCell : resolved.valueCell)),
          Option.getOrNull,
        );
      }

      // CASE B: This cell's own field has been moved away
      const ownField = isHeaderCell ? pairHeaderToField[addr] : (cellGet(cell, 'name') as string | undefined);
      if (ownField && fieldHasPosition(ownField)) {
        return null; // vacated
      }

      // CASE C: This cell's field is user-hidden (by internal or display name)
      if (ownField && hidden.has(ownField)) {
        return null;
      }

      // CASE D: API-hidden cell (not explicitly shown by user)
      if (cellGet(cell, 'hidden') === true) {
        const isExplicitlyShown = ownField && shown && shown.has(ownField);
        if (!isExplicitlyShown) return null;
      }

      return cell;
    })
    .filter((cell: ImmutableCell | null) => cell != null) as RawData;

  // ── Pass 2: inject cells remapped to positions missing from rawData ────────
  const pass2Result = positions.reduce(
    (result: RawData, targetPairHeaderAddr: string, incomingField: string) =>
      pipe(
        parseAddr(targetPairHeaderAddr),
        Option.filter((targetParts) => {
          const targetValueAddr = `${targetParts.col}${targetParts.row + 1}`;
          return !rawData.has(targetPairHeaderAddr) || !rawData.has(targetValueAddr);
        }),
        Option.flatMap((targetParts) =>
          pipe(
            resolveRemappedCells(
              incomingField,
              targetPairHeaderAddr,
              rawData,
              fieldToOriginalPairHeader,
              hidden,
              shown,
              descNameToFieldName,
            ),
            Option.map((resolved) => {
              const headerMissing = !rawData.has(targetPairHeaderAddr);
              const valueMissing = !rawData.has(`${targetParts.col}${targetParts.row + 1}`);
              const withHeader =
                headerMissing && resolved.headerCell
                  ? (result.set(targetPairHeaderAddr, resolved.headerCell) as RawData)
                  : result;
              return valueMissing && resolved.valueCell
                ? (withHeader.set(resolved.valueAddr, resolved.valueCell) as RawData)
                : withHeader;
            }),
          ),
        ),
        Option.getOrElse(() => result),
      ),
    pass1Result,
  );

  // ── Pass 3: place displaced fields at the last empty pair-slot ─────────────
  const pass3Result =
    displacedFields.length > 0
      ? (() => {
          const allPairPositions = buildAllPairPositions(rawData);

          const occupiedPairHeaders = pass2Result.reduce((acc: Set<string>, cell: ImmutableCell, addr: string) => {
            const type = cellGet(cell, 'type');
            if (type === 'description') return acc.add(addr);
            const c = cellGet(cell, '_col') as string;
            const r = cellGet(cell, '_row') as number;
            return c && r ? acc.add(`${c}${r - 1}`) : acc;
          }, Set<string>());

          const emptySlots = pipe(
            allPairPositions,
            A.filter((pos) => {
              if (occupiedPairHeaders.has(pos)) return false;
              // A slot a placement targets, or whose own field was not moved, stays that field's slot even while
              // the field is hidden — as in Customize form, which shows hidden fields so they can be shown again.
              if (targetToField[pos] !== undefined) return false;
              const homeField = pairHeaderToField[pos];
              if (homeField !== undefined) return fieldHasPosition(homeField);
              const posCell = rawData.get(pos);
              if (posCell && cellGet(posCell, 'hidden') === true) return false;
              return pipe(
                parseAddr(pos),
                Option.map((posParts) => {
                  const valCell = rawData.get(`${posParts.col}${posParts.row + 1}`);
                  return !(valCell && cellGet(valCell, 'hidden') === true);
                }),
                Option.getOrElse(() => true),
              );
            }),
            A.reverse,
          );

          return A.reduce(displacedFields, pass2Result, (result: RawData, fieldName: string, idx: number) =>
            idx >= emptySlots.length
              ? result
              : pipe(
                  resolveRemappedCells(
                    fieldName,
                    emptySlots[idx],
                    rawData,
                    fieldToOriginalPairHeader,
                    hidden,
                    shown,
                    descNameToFieldName,
                  ),
                  Option.map((resolved) => {
                    const withHeader = resolved.headerCell
                      ? (result.set(resolved.headerAddr, resolved.headerCell) as RawData)
                      : result;
                    return resolved.valueCell
                      ? (withHeader.set(resolved.valueAddr, resolved.valueCell) as RawData)
                      : withHeader;
                  }),
                  Option.getOrElse(() => result),
                ),
          );
        })()
      : pass2Result;

  // ── Pass 4: apply readonly to locked fields ──────────────────────────────
  const pass4Result =
    locked && locked.size > 0
      ? (pass3Result.map((cell: ImmutableCell) => {
          const name = cellGet(cell, 'name') as string | undefined;
          const type = cellGet(cell, 'type') as string;
          if (!name) return cell;

          if (type !== 'description' && locked.has(name)) {
            return cell.set('readonly', true);
          }

          return pipe(
            Option.fromNullable(pairHeaderToField[cellGet(cell, '_cell') as string]),
            Option.filter((pairField) => locked.has(pairField)),
            Option.map(() => cell.set('readonly', true)),
            Option.getOrElse(() => cell),
          );
        }) as unknown as RawData)
      : pass3Result;

  // ── Pass 5: re-key map entries so the map key matches the cell's _cell address.
  // Passes 1–4 may leave stale keys when a cell's _cell was changed by rearrangement
  // (e.g. Pass 1 uses .map() which preserves original keys). Tables.componentDidUpdate
  // relies on data.get(active) to find the active cell, so the key must equal _cell.
  const rekeyed = pass4Result.reduce((acc: RawData, cell: ImmutableCell, addr: string) => {
    const cellAddr = cellGet(cell, '_cell') as string | undefined;
    const key = cellAddr || addr;
    return acc.set(key, cell) as RawData;
  }, OrderedMap<string, ImmutableCell>() as RawData);

  // Sort by cell address so getMetaRange gets correct column/row order.
  return rekeyed.sortBy((_cell: ImmutableCell, addr: string) => addrSort(addr)) as RawData;
}
